/*
 * Speaking detector — taps live call MediaStreams through the Web Audio API and
 * reports who is currently talking, so the call UI can put a "wave" on the
 * speaker's avatar. Pure browser audio analysis: the RMS (loudness) of the
 * time-domain signal, with hysteresis (separate on/off thresholds) plus a short
 * "hangover" so the indicator doesn't flicker between syllables.
 *
 * Best-effort by design: if the AudioContext is unavailable (or a stream has no
 * audio track) the monitor stays inert and never throws into the call path.
 */

/** RMS (0..1) of a Float32 time-domain buffer; 0 for silence. Pure + testable. */
export function computeRms(data: Float32Array): number {
  if (!data.length) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    sum += v * v;
  }
  return Math.sqrt(sum / data.length);
}

type ChangeFn = (key: string, speaking: boolean) => void;

interface Entry {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  data: Float32Array;
  speaking: boolean;
  belowSince: number; // ms timestamp we first dropped below the OFF threshold
}

// Tuned for speech: switch ON well above room noise, OFF lower (hysteresis).
const ON_THRESHOLD = 0.045;
const OFF_THRESHOLD = 0.025;
const HANGOVER_MS = 280;

export class SpeakingMonitor {
  private ctx: AudioContext | null = null;
  private readonly entries = new Map<string, Entry>();
  private raf = 0;
  private running = false;

  constructor(private readonly onChange: ChangeFn) {}

  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const AC: typeof AudioContext | undefined =
        window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
    } catch {
      this.ctx = null;
    }
    return this.ctx;
  }

  /** Begin watching `stream` under `key`. Replaces any prior stream for that key. */
  add(key: string, stream: MediaStream): void {
    try {
      if (this.entries.has(key)) this.remove(key);
      if (!stream.getAudioTracks().length) return;
      const ctx = this.ensureCtx();
      if (!ctx) return;
      void ctx.resume?.().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.2;
      source.connect(analyser); // analysis tap only — never connected to destination
      this.entries.set(key, {
        source,
        analyser,
        data: new Float32Array(analyser.fftSize),
        speaking: false,
        belowSince: 0,
      });
      this.start();
    } catch {
      /* best-effort — a failed tap must not break the call */
    }
  }

  /** Stop watching `key`; emits a final `false` if it was mid-speech. */
  remove(key: string): void {
    const e = this.entries.get(key);
    if (!e) return;
    try { e.source.disconnect(); } catch { /* noop */ }
    try { e.analyser.disconnect(); } catch { /* noop */ }
    this.entries.delete(key);
    if (e.speaking) this.onChange(key, false);
    if (!this.entries.size) this.stop();
  }

  /** Tear everything down (call ended). */
  close(): void {
    for (const key of Array.from(this.entries.keys())) this.remove(key);
    this.stop();
    try { void this.ctx?.close(); } catch { /* noop */ }
    this.ctx = null;
  }

  private now(): number {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  }

  private start(): void {
    if (this.running || typeof requestAnimationFrame === 'undefined') return;
    this.running = true;
    const tick = () => {
      if (!this.running) return;
      const t = this.now();
      this.entries.forEach((e, key) => {
        e.analyser.getFloatTimeDomainData(e.data);
        const level = computeRms(e.data);
        if (!e.speaking) {
          if (level >= ON_THRESHOLD) {
            e.speaking = true;
            e.belowSince = 0;
            this.onChange(key, true);
          }
        } else if (level >= OFF_THRESHOLD) {
          e.belowSince = 0;
        } else if (!e.belowSince) {
          e.belowSince = t;
        } else if (t - e.belowSince >= HANGOVER_MS) {
          e.speaking = false;
          e.belowSince = 0;
          this.onChange(key, false);
        }
      });
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private stop(): void {
    this.running = false;
    if (this.raf && typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.raf);
    this.raf = 0;
  }
}
